/**
 * Syntax colouring for the text editor. Pure — no DOM, no Node.
 *
 * Tokens, not markup: the caller decides how to paint them. Deliberately
 * shallow — one pass, regex rules per language, no parse tree. It is there to
 * make a file readable, not to prove it correct.
 */

export type TokenType =
  | 'comment' | 'string' | 'number' | 'keyword' | 'type' | 'builtin'
  | 'tag' | 'attr' | 'meta' | 'punct';

/** A coloured run. Gaps between tokens are plain text. */
export interface Token {
  start: number;
  end: number;
  type: TokenType;
}

export interface Language {
  id: string;
  name: string;
  /** Extensions it claims, lower case, no dot. */
  exts: string[];
  /** Line comment marker, for comment toggling. */
  line?: string;
  /** Wrapping comment, where the language has no line form. */
  block?: [string, string];
  /** What the Tab key inserts. Spaces for code, a real tab for data files. */
  indent: string;
}

export const LANGUAGES: Language[] = [
  { id: 'plain', name: 'Plain text', exts: ['txt', 'log', 'me', 'nfo'], indent: '\t' },
  { id: 'html', name: 'HTML', exts: ['html', 'htm', 'xhtml'], block: ['<!--', '-->'], indent: '  ' },
  { id: 'xml', name: 'XML', exts: ['xml', 'svg', 'xsl', 'rss'], block: ['<!--', '-->'], indent: '  ' },
  { id: 'css', name: 'CSS', exts: ['css'], block: ['/*', '*/'], indent: '  ' },
  { id: 'javascript', name: 'JavaScript', exts: ['js', 'mjs', 'cjs', 'jsx'], line: '//', indent: '  ' },
  { id: 'typescript', name: 'TypeScript', exts: ['ts', 'mts', 'tsx'], line: '//', indent: '  ' },
  { id: 'json', name: 'JSON', exts: ['json', 'map', 'jsonc'], line: '//', indent: '  ' },
  { id: 'c', name: 'C', exts: ['c', 'h'], line: '//', indent: '  ' },
  { id: 'cpp', name: 'C++', exts: ['cpp', 'cc', 'cxx', 'hpp', 'hxx', 'ino'], line: '//', indent: '  ' },
  { id: 'php', name: 'PHP', exts: ['php', 'phtml'], line: '//', indent: '  ' },
  { id: 'python', name: 'Python', exts: ['py', 'pyw'], line: '#', indent: '    ' },
  { id: 'java', name: 'Java', exts: ['java', 'cs'], line: '//', indent: '  ' },
  { id: 'shell', name: 'Shell', exts: ['sh', 'bash', 'zsh'], line: '#', indent: '  ' },
  { id: 'sql', name: 'SQL', exts: ['sql'], line: '--', indent: '  ' },
  { id: 'ini', name: 'INI', exts: ['ini', 'cfg', 'conf', 'toml'], line: ';', indent: '  ' },
  { id: 'markdown', name: 'Markdown', exts: ['md', 'markdown'], indent: '  ' },
  { id: 'csv', name: 'CSV', exts: ['csv', 'bom', 'tsv'], indent: '\t' },
  { id: 'netlist', name: 'Net list', exts: ['net', 'nlt'], indent: '\t' },
  { id: 'gerber', name: 'Gerber', exts: ['gbr', 'gbl', 'gtl', 'gbs', 'gts', 'gbo', 'gto', 'drl'], indent: '\t' },
];

const BY_ID = new Map(LANGUAGES.map((l) => [l.id, l]));

export function languageById(id: string): Language {
  return BY_ID.get(id) ?? BY_ID.get('plain')!;
}

/** Files past this are shown plain — colouring one costs more than it is worth. */
export const HIGHLIGHT_LIMIT = 400_000;

// ---------------------------------------------------------------------------
// Detection
// ---------------------------------------------------------------------------

/**
 * Which language a file is in. Extension first, content when that says nothing.
 *
 * `.net` and `.bom` are ours and always mean what they say, so the extension is
 * trusted; `.txt` is trusted to be nothing in particular, and gets sniffed.
 */
export function detectLanguage(fileName: string, text: string): string {
  const ext = /\.([^.\\/]+)$/.exec(fileName)?.[1]?.toLowerCase() ?? '';
  if (ext) {
    const hit = LANGUAGES.find((l) => l.exts.includes(ext));
    if (hit && hit.id !== 'plain') return hit.id;
  }

  const head = text.slice(0, 4000);
  // Before the XML sniff, since both open with `<?`. A file that starts with a
  // PHP tag is PHP whatever it is called.
  if (/^\s*<\?php\b/.test(head)) return 'php';
  if (/^\s*<\?xml/i.test(head)) return 'xml';
  if (/^\s*<!doctype\s+html|<html[\s>]/i.test(head)) return 'html';
  if (/^#!.*\b(bash|sh|zsh)\b/.test(head)) return 'shell';
  if (/^#!.*\bpython/.test(head)) return 'python';
  if (/^\s*[{[]/.test(head) && /"\s*:/.test(head)) return 'json';
  if (/^%FS|^G0?4 |^\*?%MO(IN|MM)/m.test(head)) return 'gerber';
  if (/^\*NET\*/m.test(head) || /^\|[^|]*\|/m.test(head)) return 'netlist';
  // A comma-separated head with the same field count on every line is a table.
  if (looksLikeCsv(head)) return 'csv';
  return 'plain';
}

function looksLikeCsv(head: string): boolean {
  const lines = head.split(/\r?\n/).filter((l) => l.trim() !== '').slice(0, 6);
  if (lines.length < 2) return false;
  const first = lines[0]!;
  const n = countTop(first);
  if (n < 2) return false;
  return lines.every((l) => countTop(l) === n);
}

/** Commas outside quotes. */
function countTop(line: string): number {
  let n = 0;
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') quoted = !quoted;
    else if (c === ',' && !quoted) n++;
  }
  return n;
}

// ---------------------------------------------------------------------------
// The scanner
// ---------------------------------------------------------------------------

/** A rule matches at the cursor; `word` looks the match up in the word sets. */
interface Rule {
  type: TokenType;
  re: RegExp;
  word?: boolean;
  /** Only try this rule where what came before allows it. */
  guard?: (text: string, at: number) => boolean;
}

interface WordSets {
  keyword?: Set<string>;
  type?: Set<string>;
  builtin?: Set<string>;
  /** Match words without regard to case (SQL). */
  fold?: boolean;
}

const set = (s: string): Set<string> => new Set(s.split(/\s+/).filter(Boolean));

const NUMBER = /0[xXbBoO][0-9a-fA-F_]+[uUlL]*|(?:\d[\d_]*)?\.?\d[\d_]*(?:[eE][+-]?\d+)?[uUlLfF]*/y;
const IDENT = /[A-Za-z_$][A-Za-z0-9_$]*/y;
const DQ = /"(?:\\.|[^"\\\n])*"?/y;
const SQ = /'(?:\\.|[^'\\\n])*'?/y;
// A template literal may run over lines, but only a closed one. Left open, it
// stops at its own line rather than staining the rest of the file — an odd
// backtick is far likelier to be a typo, or one inside a regular expression,
// than a string a thousand lines long.
const BACKTICK = /`(?:\\.|[^`\\])*`/y;
const BACKTICK_OPEN = /`[^\n]*/y;

/**
 * A regular expression literal.
 *
 * Without this the slashes are division and the quotes and backticks inside
 * one are read as strings — `/["'`]/` alone would open a string that runs to
 * whatever closes it, tens of lines away.
 */
const REGEX_LITERAL = /\/(?![*/])(?:\\.|\[(?:\\.|[^\]\\\n])*\]|[^/\\\n[])+\/[dgimsuvy]*/y;

/** Words after which a slash starts a pattern rather than a division. */
const BEFORE_REGEX = set(`return typeof instanceof in of new delete void do
  else case throw yield await`);

/**
 * Whether a `/` here can open a pattern.
 *
 * The rule every highlighter uses: after a value — a name, a number, a closing
 * bracket — a slash divides; after anything else it opens a pattern.
 */
function regexAllowedAt(text: string, at: number): boolean {
  let j = at - 1;
  while (j >= 0 && /\s/.test(text[j]!)) j--;
  if (j < 0) return true;
  const c = text[j]!;
  if (c === ')' || c === ']') return false;
  if (/[A-Za-z0-9_$]/.test(c)) {
    let k = j;
    while (k >= 0 && /[A-Za-z0-9_$]/.test(text[k]!)) k--;
    return BEFORE_REGEX.has(text.slice(k + 1, j + 1));
  }
  return true;
}
const BLOCK_COMMENT = /\/\*[\s\S]*?(?:\*\/|$)/y;
const SLASH_COMMENT = /\/\/[^\n]*/y;
const HASH_COMMENT = /#[^\n]*/y;
const OPERATOR = /[+\-*/%=<>!&|^~?:]+|[{}()[\];,.]/y;

/** Run the rules along the text, appending tokens. Unmatched characters are plain. */
function scan(
  text: string, rules: Rule[], words: WordSets | undefined,
  out: Token[], base = 0, from = 0, to = text.length,
): void {
  let i = from;
  while (i < to) {
    let hit = false;
    for (const rule of rules) {
      if (rule.guard && !rule.guard(text, i)) continue;
      rule.re.lastIndex = i;
      const m = rule.re.exec(text);
      if (!m || m[0].length === 0 || i + m[0].length > to) continue;
      const end = i + m[0].length;
      if (rule.word) {
        const type = classify(m[0], words);
        if (type) out.push({ start: base + i, end: base + end, type });
      } else {
        out.push({ start: base + i, end: base + end, type: rule.type });
      }
      i = end;
      hit = true;
      break;
    }
    if (!hit) i++;
  }
}

function classify(word: string, words: WordSets | undefined): TokenType | null {
  if (!words) return null;
  const w = words.fold ? word.toLowerCase() : word;
  if (words.keyword?.has(w)) return 'keyword';
  if (words.type?.has(w)) return 'type';
  if (words.builtin?.has(w)) return 'builtin';
  return null;
}

// ---------------------------------------------------------------------------
// Word sets
// ---------------------------------------------------------------------------

const JS_WORDS: WordSets = {
  keyword: set(`break case catch class const continue debugger default delete do
    else export extends finally for function if import in instanceof let new of
    return static super switch this throw try typeof var void while with yield
    async await from as`),
  builtin: set(`true false null undefined NaN Infinity console window document
    globalThis Math JSON Object Array String Number Boolean Promise Map Set Date
    RegExp Error require module exports`),
};

const TS_WORDS: WordSets = {
  keyword: new Set([...JS_WORDS.keyword!, ...set(`interface type enum namespace
    declare implements readonly public private protected abstract satisfies
    keyof infer is asserts override accessor`)]),
  type: set(`any unknown never number string boolean void object symbol bigint
    Partial Required Readonly Record Pick Omit`),
  builtin: JS_WORDS.builtin!,
};

const C_WORDS: WordSets = {
  keyword: set(`alignas alignof and asm break case catch class concept const
    consteval constexpr constinit const_cast continue co_await co_return co_yield
    decltype default delete do dynamic_cast else enum explicit export extern for
    friend goto if inline mutable namespace new noexcept operator or private
    protected public register reinterpret_cast requires return sizeof static
    static_assert static_cast struct switch template this throw try typedef
    typeid typename union using virtual volatile while not xor`),
  type: set(`auto bool char char8_t char16_t char32_t double float int long short
    signed unsigned void wchar_t size_t ssize_t ptrdiff_t int8_t int16_t int32_t
    int64_t uint8_t uint16_t uint32_t uint64_t FILE string vector map set pair`),
  builtin: set(`true false nullptr NULL std cout cin cerr endl printf sprintf
    malloc free memcpy memset strlen strcpy this_thread`),
};

const PHP_WORDS: WordSets = {
  keyword: set(`abstract and as break callable case catch class clone const
    continue declare default do echo else elseif empty enddeclare endfor
    endforeach endif endswitch endwhile enum extends final finally fn for foreach
    function global goto if implements include include_once instanceof insteadof
    interface isset list match namespace new or print private protected public
    readonly require require_once return static switch throw trait try unset use
    var while xor yield`),
  type: set(`array bool boolean callable double float int integer iterable mixed
    never object parent self string void`),
  builtin: set(`true false null TRUE FALSE NULL $this __DIR__ __FILE__ __LINE__
    __CLASS__ __FUNCTION__ __METHOD__ __NAMESPACE__ die exit header count strlen
    substr sprintf printf str_replace implode explode trim json_encode json_decode
    array_map array_filter array_keys array_values in_array is_array
    preg_match preg_replace file_get_contents file_put_contents var_dump print_r
    number_format htmlspecialchars`),
};

const PY_WORDS: WordSets = {
  keyword: set(`and as assert async await break class continue def del elif else
    except finally for from global if import in is lambda nonlocal not or pass
    raise return try while with yield match case`),
  builtin: set(`None True False self cls print len range int str float list dict
    set tuple bool open type isinstance issubclass super enumerate zip map filter
    sorted sum min max abs round repr format input`),
};

const JAVA_WORDS: WordSets = {
  keyword: set(`abstract assert break case catch class const continue default do
    else enum extends final finally for goto if implements import instanceof
    interface native new package private protected public return static strictfp
    super switch synchronized this throw throws transient try volatile while var
    record sealed permits yield namespace using`),
  type: set(`boolean byte char double float int long short void String Integer
    Double Boolean Object List Map Set var`),
  builtin: set(`true false null this System out println args length toString
    equals hashCode`),
};

const SHELL_WORDS: WordSets = {
  keyword: set(`if then else elif fi for while until do done case esac in function
    return export local readonly declare shift exit source trap set unset break
    continue`),
  builtin: set(`echo cd pwd ls cat grep sed awk cut sort uniq head tail test read
    printf mkdir rm cp mv chmod find xargs`),
};

const SQL_WORDS: WordSets = {
  fold: true,
  keyword: set(`select from where insert into values update set delete create
    table drop alter add column primary key foreign references index unique view
    join inner left right full outer cross on using group by order having limit
    offset union all distinct as and or not null is like ilike in between exists
    case when then else end begin commit rollback transaction with returning
    constraint default cascade grant revoke replace if temporary procedure
    function trigger declare`),
  type: set(`int integer bigint smallint tinyint decimal numeric float double
    real char varchar text blob date datetime timestamp time boolean bool json
    serial uuid enum binary`),
  builtin: set(`count sum avg min max coalesce nullif cast concat now current_date
    current_timestamp length substring upper lower trim round abs auto_increment`),
};

// ---------------------------------------------------------------------------
// Rule sets
// ---------------------------------------------------------------------------

const JS_RULES: Rule[] = [
  { type: 'comment', re: BLOCK_COMMENT },
  { type: 'comment', re: SLASH_COMMENT },
  // Before the operator rule, which would otherwise take the opening slash and
  // leave the pattern's own quotes to be read as strings.
  { type: 'meta', re: REGEX_LITERAL, guard: regexAllowedAt },
  { type: 'string', re: DQ },
  { type: 'string', re: SQ },
  { type: 'string', re: BACKTICK },
  { type: 'string', re: BACKTICK_OPEN },
  { type: 'number', re: NUMBER },
  { type: 'punct', re: IDENT, word: true },
  { type: 'punct', re: OPERATOR },
];

const C_RULES: Rule[] = [
  { type: 'comment', re: BLOCK_COMMENT },
  { type: 'comment', re: SLASH_COMMENT },
  // A preprocessor line, continuations and all.
  { type: 'meta', re: /#[ \t]*[a-z_]+(?:\\\r?\n|[^\n])*/y },
  { type: 'string', re: DQ },
  { type: 'string', re: SQ },
  { type: 'number', re: NUMBER },
  { type: 'punct', re: IDENT, word: true },
  { type: 'punct', re: OPERATOR },
];

/**
 * A heredoc or nowdoc body, `<<<END … END`.
 *
 * Worth having even where a file has none: an opening `<<<` read as three
 * less-than signs leaves the body to be scanned as code, and the first
 * apostrophe in a line of English prose then opens a string that runs to
 * whatever closes it, pages away. The backreference ties the terminator to the
 * label, so a body mentioning some other label does not end it early.
 */
const HEREDOC = /<<<[ \t]*(['"]?)([A-Za-z_]\w*)\1\r?\n[\s\S]*?\n[ \t]*\2/y;

const PHP_RULES: Rule[] = [
  { type: 'comment', re: BLOCK_COMMENT },
  { type: 'comment', re: SLASH_COMMENT },
  // Before the hash comment, or `#[Attribute]` — which is PHP 8 syntax, not a
  // remark — would be greyed out to the end of its line along with whatever
  // code follows it there.
  { type: 'meta', re: /#\[[^\]\n]*\]?/y },
  { type: 'comment', re: HASH_COMMENT },
  { type: 'string', re: HEREDOC },
  { type: 'string', re: DQ },
  { type: 'string', re: SQ },
  // A variable, and the variable-variable `$$name`. Before IDENT, which would
  // otherwise take the `$` as the head of an ordinary word.
  { type: 'builtin', re: /\$\$?[A-Za-z_]\w*/y },
  { type: 'number', re: NUMBER },
  { type: 'punct', re: IDENT, word: true },
  { type: 'punct', re: OPERATOR },
];

const PY_RULES: Rule[] = [
  { type: 'comment', re: HASH_COMMENT },
  { type: 'string', re: /[rRbBfFuU]{0,2}"""[\s\S]*?(?:"""|$)/y },
  { type: 'string', re: /[rRbBfFuU]{0,2}'''[\s\S]*?(?:'''|$)/y },
  { type: 'string', re: /[rRbBfFuU]{0,2}"(?:\\.|[^"\\\n])*"?/y },
  { type: 'string', re: /[rRbBfFuU]{0,2}'(?:\\.|[^'\\\n])*'?/y },
  { type: 'meta', re: /@[A-Za-z_][\w.]*/y },
  { type: 'number', re: NUMBER },
  { type: 'punct', re: IDENT, word: true },
  { type: 'punct', re: OPERATOR },
];

const SHELL_RULES: Rule[] = [
  { type: 'comment', re: HASH_COMMENT },
  { type: 'string', re: DQ },
  { type: 'string', re: SQ },
  { type: 'builtin', re: /\$(?:\{[^}\n]*\}|[A-Za-z_]\w*|[@*#?$!0-9])/y },
  { type: 'number', re: NUMBER },
  { type: 'punct', re: IDENT, word: true },
  { type: 'punct', re: OPERATOR },
];

const SQL_RULES: Rule[] = [
  { type: 'comment', re: /--[^\n]*/y },
  { type: 'comment', re: BLOCK_COMMENT },
  { type: 'string', re: SQ },
  { type: 'attr', re: /"(?:[^"\n]|"")*"?|`[^`\n]*`?|\[[^\]\n]*\]?/y },
  { type: 'number', re: NUMBER },
  { type: 'punct', re: IDENT, word: true },
  { type: 'punct', re: OPERATOR },
];

const JSON_RULES: Rule[] = [
  { type: 'comment', re: SLASH_COMMENT },
  { type: 'comment', re: BLOCK_COMMENT },
  // A string followed by a colon is a key, whatever is inside it.
  { type: 'attr', re: /"(?:\\.|[^"\\\n])*"(?=\s*:)/y },
  { type: 'string', re: DQ },
  { type: 'number', re: /-?(?:0|[1-9]\d*)(?:\.\d+)?(?:[eE][+-]?\d+)?/y },
  { type: 'keyword', re: /\b(?:true|false|null)\b/y },
  { type: 'punct', re: OPERATOR },
];

const INI_RULES: Rule[] = [
  { type: 'comment', re: /^[ \t]*[;#][^\n]*/my },
  { type: 'tag', re: /^[ \t]*\[[^\]\n]*\]/my },
  { type: 'attr', re: /^[ \t]*[^=:\n[\]]+(?=[=:])/my },
  { type: 'string', re: DQ },
  { type: 'string', re: SQ },
  { type: 'number', re: NUMBER },
  { type: 'keyword', re: /\b(?:true|false|yes|no|on|off)\b/y },
  { type: 'punct', re: /[=:]/y },
];

const MD_RULES: Rule[] = [
  { type: 'string', re: /^[ \t]*```[\s\S]*?(?:```|$)/my },
  { type: 'tag', re: /^#{1,6}[ \t][^\n]*/my },
  { type: 'comment', re: /^[ \t]*>[^\n]*/my },
  { type: 'punct', re: /^[ \t]*(?:[-*+]|\d+\.)[ \t]/my },
  { type: 'string', re: /`[^`\n]*`?/y },
  { type: 'attr', re: /!?\[[^\]\n]*\]\([^)\n]*\)/y },
  { type: 'keyword', re: /\*\*[^*\n]+\*\*|__[^_\n]+__/y },
  { type: 'meta', re: /^[ \t]*(?:---|\*\*\*|___)[ \t]*$/my },
];

const GERBER_RULES: Rule[] = [
  { type: 'comment', re: /G0?4[^*\n]*\*?|;[^\n]*/y },
  { type: 'meta', re: /%[^%]*%?/y },
  { type: 'keyword', re: /[GDMTHAFB]\d+/y },
  { type: 'number', re: /[XYIJZCSR][+-]?[\d.]+/y },
  { type: 'punct', re: /\*/y },
];

const RULES: Record<string, { rules: Rule[]; words?: WordSets }> = {
  javascript: { rules: JS_RULES, words: JS_WORDS },
  typescript: { rules: JS_RULES, words: TS_WORDS },
  c: { rules: C_RULES, words: C_WORDS },
  cpp: { rules: C_RULES, words: C_WORDS },
  java: { rules: C_RULES, words: JAVA_WORDS },
  // Reached through the markup pass, which hands it each island; registered
  // here too so a caller naming the language directly gets its rules.
  php: { rules: PHP_RULES, words: PHP_WORDS },
  python: { rules: PY_RULES, words: PY_WORDS },
  shell: { rules: SHELL_RULES, words: SHELL_WORDS },
  sql: { rules: SQL_RULES, words: SQL_WORDS },
  json: { rules: JSON_RULES },
  ini: { rules: INI_RULES },
  markdown: { rules: MD_RULES },
  gerber: { rules: GERBER_RULES },
};

// ---------------------------------------------------------------------------
// Colour a file
// ---------------------------------------------------------------------------

/** Tokens for `text`, in order and never overlapping. */
export function tokenize(text: string, langId: string): Token[] {
  if (text.length > HIGHLIGHT_LIMIT) return [];
  const out: Token[] = [];
  switch (langId) {
    case 'html':
      tokenizeMarkup(text, out, true);
      break;
    case 'xml':
      tokenizeMarkup(text, out, false);
      break;
    case 'php':
      // Markup with a third embedded language, script and style included: a
      // .php page is an HTML page that happens to have code in it.
      tokenizeMarkup(text, out, true, true);
      break;
    case 'css':
      tokenizeCss(text, out, 0, 0, text.length);
      break;
    case 'csv':
      tokenizeCsv(text, out);
      break;
    case 'netlist':
      tokenizeNetlist(text, out);
      break;
    default: {
      const spec = RULES[langId];
      if (spec) scan(text, spec.rules, spec.words, out);
    }
  }
  return out;
}

// --- Markup -----------------------------------------------------------------

/**
 * HTML and XML. Tags, attributes, entities and comments, with `<script>` and
 * `<style>` bodies handed to the language they are written in.
 *
 * `php` adds a third embedded language, and one rule covers both shapes a PHP
 * file comes in: `api.php` opens with `<?php` and is code to the end, while
 * `index.php` is markup with one island in the middle of it. A file that is all
 * code is simply a file whose first island starts at the top.
 */
function tokenizeMarkup(text: string, out: Token[], embed: boolean, php = false): void {
  const n = text.length;
  let i = 0;
  while (i < n) {
    const lt = text.indexOf('<', i);
    if (lt < 0) { entities(text, out, i, n); break; }
    if (lt > i) entities(text, out, i, lt);

    // A PHP island. Only the two tags that are unambiguously PHP: a bare `<?`
    // is a short open tag, off by default for twenty years, and claiming it
    // would swallow the `<?xml` declaration of a perfectly ordinary XML file.
    if (php && /^<\?(?:php\b|=)/.test(text.slice(lt, lt + 6))) {
      const open = text.startsWith('<?=', lt) ? 3 : 5;
      out.push({ start: lt, end: lt + open, type: 'meta' });
      const close = text.indexOf('?>', lt + open);
      const body = close < 0 ? n : close;
      if (body > lt + open) scan(text, PHP_RULES, PHP_WORDS, out, 0, lt + open, body);
      // The closing tag is optional, and a file that is all code conventionally
      // leaves it off — so running to the end is the normal case, not a fault.
      if (close >= 0) out.push({ start: close, end: close + 2, type: 'meta' });
      i = close < 0 ? n : close + 2;
      continue;
    }

    if (text.startsWith('<!--', lt)) {
      const end = text.indexOf('-->', lt + 4);
      const stop = end < 0 ? n : end + 3;
      out.push({ start: lt, end: stop, type: 'comment' });
      i = stop;
      continue;
    }
    if (text.startsWith('<!', lt) || text.startsWith('<?', lt)) {
      const end = text.indexOf('>', lt);
      const stop = end < 0 ? n : end + 1;
      out.push({ start: lt, end: stop, type: 'meta' });
      i = stop;
      continue;
    }

    const nameMatch = /<\/?([A-Za-z][\w:.-]*)/y;
    nameMatch.lastIndex = lt;
    const m = nameMatch.exec(text);
    if (!m) { out.push({ start: lt, end: lt + 1, type: 'punct' }); i = lt + 1; continue; }

    out.push({ start: lt, end: lt + m[0].length, type: 'tag' });
    i = lt + m[0].length;

    // Attributes, up to the closing angle bracket.
    let selfClosed = false;
    while (i < n) {
      const c = text[i]!;
      if (c === '>') { out.push({ start: i, end: i + 1, type: 'punct' }); i++; break; }
      if (c === '/' && text[i + 1] === '>') {
        out.push({ start: i, end: i + 2, type: 'punct' });
        i += 2;
        selfClosed = true;
        break;
      }
      if (/\s/.test(c)) { i++; continue; }
      const attr = /[^\s=/>]+/y;
      attr.lastIndex = i;
      const a = attr.exec(text);
      if (a) {
        out.push({ start: i, end: i + a[0].length, type: 'attr' });
        i += a[0].length;
        continue;
      }
      if (c === '=') {
        out.push({ start: i, end: i + 1, type: 'punct' });
        i++;
        const val = /"[^"]*"?|'[^']*'?|[^\s>]+/y;
        val.lastIndex = i;
        const v = val.exec(text);
        if (v) { out.push({ start: i, end: i + v[0].length, type: 'string' }); i += v[0].length; }
        continue;
      }
      i++;
    }

    if (!embed || selfClosed || m[0][1] === '/') continue;
    const tag = m[1]!.toLowerCase();
    if (tag !== 'script' && tag !== 'style') continue;

    // The body of a script or style block belongs to another language.
    const at = text.slice(i).search(new RegExp(`</${tag}`, 'i'));
    const bodyEnd = at < 0 ? n : i + at;
    if (bodyEnd > i) {
      if (tag === 'style') tokenizeCss(text, out, 0, i, bodyEnd);
      else scan(text, JS_RULES, JS_WORDS, out, 0, i, bodyEnd);
    }
    i = bodyEnd;
  }
}

/** `&amp;` and friends, inside a run of markup text. */
function entities(text: string, out: Token[], from: number, to: number): void {
  const re = /&(?:#\d+|#[xX][0-9a-fA-F]+|[A-Za-z][A-Za-z0-9]{1,31});/y;
  let i = from;
  while (i < to) {
    const amp = text.indexOf('&', i);
    if (amp < 0 || amp >= to) break;
    re.lastIndex = amp;
    const m = re.exec(text);
    if (m && amp + m[0].length <= to) {
      out.push({ start: amp, end: amp + m[0].length, type: 'meta' });
      i = amp + m[0].length;
    } else {
      i = amp + 1;
    }
  }
}

// --- CSS --------------------------------------------------------------------

/**
 * Stylesheets. The brace stack is the whole state machine: in a declaration
 * block a word is a property or a value, anywhere else it is a selector.
 *
 * What opened the block decides, not how deep it is — `@media { .a { } }` puts
 * selectors one level in, and depth alone would call them values.
 */
function tokenizeCss(text: string, out: Token[], base: number, from: number, to: number): void {
  let i = from;
  const stack: boolean[] = [];
  let atRule = false;
  const inDecl = (): boolean => stack[stack.length - 1] === true;
  while (i < to) {
    const c = text[i]!;
    if (c === '/' && text[i + 1] === '*') {
      const end = text.indexOf('*/', i + 2);
      const stop = end < 0 ? to : Math.min(to, end + 2);
      out.push({ start: base + i, end: base + stop, type: 'comment' });
      i = stop;
      continue;
    }
    if (c === '"' || c === "'") {
      const re = c === '"' ? DQ : SQ;
      re.lastIndex = i;
      const m = re.exec(text);
      const len = m ? m[0].length : 1;
      out.push({ start: base + i, end: base + i + len, type: 'string' });
      i += len;
      continue;
    }
    if (c === '{') {
      stack.push(!atRule);
      atRule = false;
      out.push({ start: base + i, end: base + i + 1, type: 'punct' });
      i++;
      continue;
    }
    if (c === '}') {
      stack.pop();
      out.push({ start: base + i, end: base + i + 1, type: 'punct' });
      i++;
      continue;
    }
    if (c === ';') { atRule = false; out.push({ start: base + i, end: base + i + 1, type: 'punct' }); i++; continue; }
    if (c === '@') {
      atRule = true;
      const re = /@[\w-]*/y;
      re.lastIndex = i;
      const m = re.exec(text);
      const len = m ? m[0].length : 1;
      out.push({ start: base + i, end: base + i + len, type: 'keyword', });
      i += len;
      continue;
    }
    if (c === '!') {
      const re = /![\w-]*/y;
      re.lastIndex = i;
      const m = re.exec(text);
      const len = m ? m[0].length : 1;
      out.push({ start: base + i, end: base + i + len, type: 'keyword' });
      i += len;
      continue;
    }
    if (c === '#' && inDecl()) {
      const re = /#[0-9a-fA-F]{3,8}\b/y;
      re.lastIndex = i;
      const m = re.exec(text);
      if (m) {
        out.push({ start: base + i, end: base + i + m[0].length, type: 'number' });
        i += m[0].length;
        continue;
      }
    }
    if (!inDecl() && (c === '#' || c === '.' || c === ':' || /[A-Za-z*[]/.test(c))) {
      const re = /[#.:*]?[\w-]+(?:\([^)\n]*\))?|\[[^\]\n]*\]?/y;
      re.lastIndex = i;
      const m = re.exec(text);
      if (m) {
        out.push({ start: base + i, end: base + i + m[0].length, type: 'tag' });
        i += m[0].length;
        continue;
      }
    }
    if (inDecl() && /[A-Za-z-]/.test(c)) {
      const re = /[\w-]+/y;
      re.lastIndex = i;
      const m = re.exec(text);
      if (m) {
        const after = /[ \t]*:/y;
        after.lastIndex = i + m[0].length;
        const isProp = after.exec(text) !== null;
        out.push({
          start: base + i, end: base + i + m[0].length,
          type: isProp ? 'attr' : 'builtin',
        });
        i += m[0].length;
        continue;
      }
    }
    if (/[\d.]/.test(c)) {
      const re = /[+-]?(?:\d*\.)?\d+(?:%|[a-zA-Z]+)?/y;
      re.lastIndex = i;
      const m = re.exec(text);
      if (m) {
        out.push({ start: base + i, end: base + i + m[0].length, type: 'number' });
        i += m[0].length;
        continue;
      }
    }
    i++;
  }
}

// --- Our own data files -----------------------------------------------------

/** A table: the header row named, quoted fields as strings, numbers as numbers. */
function tokenizeCsv(text: string, out: Token[]): void {
  const n = text.length;
  let i = 0;
  let line = 0;
  let fieldStart = 0;
  let quoted = false;

  const close = (end: number): void => {
    let s = fieldStart;
    let e = end;
    while (s < e && /[ \t]/.test(text[s]!)) s++;
    while (e > s && /[ \t\r]/.test(text[e - 1]!)) e--;
    if (e <= s) return;
    const body = text.slice(s, e);
    const type: TokenType | null = line === 0 ? 'attr'
      : body[0] === '"' ? 'string'
        : /^[+-]?[\d.]+(?:[eE][+-]?\d+)?$/.test(body) ? 'number'
          : null;
    if (type) out.push({ start: s, end: e, type });
  };

  while (i < n) {
    const c = text[i]!;
    if (c === '"') { quoted = !quoted; i++; continue; }
    if (!quoted && c === ',') {
      close(i);
      out.push({ start: i, end: i + 1, type: 'punct' });
      i++;
      fieldStart = i;
      continue;
    }
    if (!quoted && c === '\n') {
      close(i);
      i++;
      line++;
      fieldStart = i;
      continue;
    }
    i++;
  }
  close(n);
}

/**
 * The original's net list: `|Signal|Nodes` rows under a `*NET*` banner, with a
 * `Contents:` / `Source:` / `Date:` header above them.
 */
function tokenizeNetlist(text: string, out: Token[]): void {
  let at = 0;
  for (const line of text.split('\n')) {
    const len = line.length;
    const body = line.replace(/\r$/, '');
    if (/^\*/.test(body) || /^END OF FILE/i.test(body)) {
      out.push({ start: at, end: at + body.length, type: 'keyword' });
    } else if (body.startsWith('|')) {
      // Bars are the separator; the field between the first two is the signal.
      const bars: number[] = [];
      for (let i = 0; i < body.length; i++) if (body[i] === '|') bars.push(i);
      const first = bars[0];
      const second = bars[1];
      for (const i of bars) {
        // In order, or the renderer would walk backwards over the name.
        if (i === second && first !== undefined && second > first + 1) {
          out.push({ start: at + first + 1, end: at + second, type: 'tag' });
        }
        out.push({ start: at + i, end: at + i + 1, type: 'punct' });
      }
    } else {
      const m = /^([A-Za-z][\w ]*):/.exec(body);
      if (m) out.push({ start: at, end: at + m[1]!.length + 1, type: 'attr' });
    }
    at += len + 1;
  }
}
